import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import { useApiCall } from '../hooks/useApiCall';
import { 
  RiHeart3Line,
  RiDeleteBin6Line,
  RiShoppingCartLine,
  RiArrowRightLine,
  RiShare2Line,
  RiEyeLine,
  RiLoader4Line
} from 'react-icons/ri';
import '../styles/Wishlist.css';
import '../styles/Cart.css';

export default function Wishlist() {
  const navigate = useNavigate();
  const { account } = useAccount();
  const { refreshCounts, guestWishlist, toggleGuestWishlist } = useCart();
  const { showSuccess, showError } = useToast();
  const apiCall = useApiCall();
  
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemoving, setIsRemoving] = useState({});

  // Fetch wishlist on component mount
  useEffect(() => {
    if (account) {
      fetchWishlist();
    } else {
      setIsLoading(false);
    }
  }, [account]);

  const fetchWishlist = async () => {
    if (!account) return;
    
    setIsLoading(true);
    try {
      const response = await apiCall('account/wishlist/get', 'POST', {}, account, null);
      
      if (response.success) {
        setWishlistItems(response.data.wishlist || []);
      } else {
        showError('Failed to load wishlist');
        console.error('Wishlist fetch failed:', response.message);
      }
    } catch (error) {
      showError('Failed to load wishlist');
      console.error('Error fetching wishlist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    if (!account) {
      showError('Please sign in to manage your wishlist');
      return;
    }

    setIsRemoving(prev => ({ ...prev, [productId]: true }));

    try {
      const response = await apiCall('account/wishlist/remove', 'POST', {
        product_id: productId
      }, account, null);
      
      if (response.success) {
        setWishlistItems(items => items.filter(item => item.id !== productId));
        refreshCounts(); // Refresh counts after removing from wishlist
      } else {
        showError(response.message || 'Failed to remove item from wishlist');
      }
    } catch (error) {
      showError('Failed to remove item from wishlist');
      console.error('Error removing from wishlist:', error);
    } finally {
      setIsRemoving(prev => ({ ...prev, [productId]: false }));
    }
  };

  const addToCart = async (item) => {
    if (!account) {
      showError('Please sign in to add items to cart');
      return;
    }
    
    if (!item.in_stock) {
      showError('Product is out of stock');
      return;
    }

    try {
      // Prepare cart data with options if they exist
      const cartData = {
        product_id: item.id,
        qty: 1
      };
      
      // Include options (modifiers) if they exist
      if (item.options) {
        cartData.options = typeof item.options === 'string' ? item.options : JSON.stringify(item.options);
      }
      
      const response = await apiCall('account/cart/add', 'POST', cartData, account, null);
      
      if (response.success) {
        showSuccess(`${item.name} added to cart!`, 4000, () => navigate('/cart'));


        // Remove from wishlist after adding to cart
        await removeFromWishlist(item.id);
        refreshCounts(); // Refresh counts after adding to cart
      } else {
        showError(response.message || 'Failed to add item to cart');
      }
    } catch (error) {
      showError('Failed to add item to cart');
      console.error('Error adding to cart:', error);
    }
  };

  const viewProduct = (id) => {
    navigate(`/products/product/${id}`);
  };

  const shareProduct = async (item) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: item.name,
          text: `Check out this ${item.name}`,
          url: window.location.origin + `/products/product/${item.id}`
        });
      } catch (error) {
        // User cancelled the share dialog or share failed
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          showError('Failed to share product');
        }
        // Do nothing if user just cancelled (AbortError)
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      try {
        await navigator.clipboard.writeText(window.location.origin + `/products/product/${item.id}`);
        showSuccess('Product link copied to clipboard!');
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        showError('Failed to copy link to clipboard');
      }
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="wishlist-page">
        <div className="wishlist-container">
          <div className="wishlist-header">
            <h1 className="wishlist-title">My wishlist</h1>
          </div>
          
          <div className="wishlist-loading">
            <RiLoader4Line className="loading-spinner" />
            <p>Loading your wishlist...</p>
          </div>
        </div>
      </div>
    );
  }

  // Not signed in
  if (!account) {
    if (guestWishlist.length === 0) {
      return (
        <div className="wishlist-page">
          <div className="wishlist-container">
            <div className="wishlist-header">
              <h1 className="wishlist-title">My wishlist</h1>
            </div>
            
            <div className="wishlist-empty">
              <RiHeart3Line className="wishlist-empty-icon" />
              <h2>Sign in to view your wishlist</h2>
              <p>Save items you love and access them anywhere</p>
              <button 
                className="btn-browse-products"
                onClick={() => navigate('/sign-in')}
              >
                Sign in
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Guest wishlist (no account but has saved items)
    return (
      <div className="wishlist-page">
        <div className="wishlist-container">
          <div className="wishlist-header">
            <h1 className="wishlist-title">My wishlist</h1>
            <span className="wishlist-items-count">{guestWishlist.length} item{guestWishlist.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="cart-guest-banner">
            <RiHeart3Line />
            <span>You're saving as a guest — <button onClick={() => navigate('/sign-in?ref=wishlist')}>Sign in</button> to keep your wishlist across devices.</span>
          </div>

          <div className="wishlist-content">
            <div className="wishlist-grid">
              {guestWishlist.map(item => (
                <div key={item.product_id} className="wishlist-item">
                  <div className="wishlist-item-image" style={{ cursor: 'pointer' }} onClick={() => navigate(`/products/product/${item.product_id}`)}>                    
                    {item.image
                      ? <img src={item.image} alt={item.name || item.title} onError={e => e.target.style.display='none'} />
                      : <div style={{ width: '100%', paddingBottom: '100%', background: '#f0ede9' }} />}
                  </div>

                  <div className="wishlist-item-details">
                    <h3 className="item-name" style={{ cursor: 'pointer' }} onClick={() => navigate(`/products/product/${item.product_id}`)}>{item.name || item.title}</h3>
                    <div className="item-pricing">
                      {item.price && <span className="price-current">{item.price_curr} {parseFloat(item.price).toFixed(2)}</span>}
                    </div>
                  </div>

                  <div className="wishlist-item-actions">
                    <div className="action-buttons">
                      <button className="btn-remove" onClick={() => toggleGuestWishlist(item)} title="Remove from wishlist">
                        <RiDeleteBin6Line />
                      </button>
                    </div>
                    <button className="btn-add-to-cart" onClick={() => navigate(`/products/product/${item.product_id}`)}>                      
                      <RiShoppingCartLine />
                      View product
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="wishlist-actions">
              <button className="btn-continue-shopping" onClick={() => navigate('/products')}>
                Continue shopping
                <RiArrowRightLine />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty wishlist
  if (wishlistItems.length === 0) {
    return (
      <div className="wishlist-page">
        <div className="wishlist-container">
          <div className="wishlist-header">
            <h1 className="wishlist-title">My wishlist</h1>
          </div>
          
          <div className="wishlist-empty">
            <RiHeart3Line className="wishlist-empty-icon" />
            <h2>Your wishlist is empty</h2>
            <p>Save items you love to your wishlist</p>
            <button 
              className="btn-browse-products"
              onClick={() => navigate('/products')}
            >
              Browse products
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-page">
      <div className="wishlist-container">
        
        <div className="wishlist-header">
          <h1 className="wishlist-title">My wishlist</h1>
          <span className="wishlist-items-count">{wishlistItems.length} items</span>
        </div>

        <div className="wishlist-content">
          <div className="wishlist-grid">
            {wishlistItems.map(item => {
              const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
              let currentPrice = hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
              let originalPrice = hasDiscount ? parseFloat(item.price) : null;
              
              // Modifier amount replaces base price (versions/variants)
              if (item.options) {
                try {
                  const options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
                  if (options && options.modifiers && Array.isArray(options.modifiers)) {
                    const priceModifier = options.modifiers.find(m => m.option && parseFloat(m.option.amount) > 0);
                    if (priceModifier) {
                      currentPrice = parseFloat(priceModifier.option.amount);
                      originalPrice = null; // variant price is the actual price
                    }
                  }
                } catch (e) {
                  console.error('Error parsing options for price calculation:', e);
                }
              }
              
              const discountPercent = hasDiscount && originalPrice ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100) : null;
              
              return (
                <div key={item.id} className="wishlist-item">
                  
                  <div className="wishlist-item-image">
                    <img 
                      src={item.primary_image} 
                      alt={item.name} 
                    />
                    {discountPercent && (
                      <div className="discount-badge">
                        -{discountPercent}%
                      </div>
                    )}
                    {!item.in_stock && (
                      <div className="stock-overlay">
                        <span>Out of Stock</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="wishlist-item-details">
                    <div className="item-brand">{item.category_name || 'General'}</div>
                    <h3 className="item-name">{item.name}</h3>
                    {item.descr && (
                      <p className="item-description">{item.descr}</p>
                    )}
                    {item.options && (() => {
                      try {
                        const options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
                        if (options && options.modifiers && options.modifiers.length > 0) {
                          return (
                            <div className="item-modifiers">
                              {options.modifiers.map((modifier, index) => (
                                <div key={index} className="modifier-item">
                                  <strong>{modifier.name}:</strong> {modifier.option?.item || 'N/A'}
                                  {modifier.option?.amount > 0 && <span className="modifier-price"> — {item.price_curr} {parseFloat(modifier.option.amount).toFixed(2)}</span>}
                                </div>
                              ))}
                            </div>
                          );
                        }
                      } catch (e) {
                        console.error('Error parsing options:', e);
                      }
                      return null;
                    })()}
                    
                    <div className="item-pricing">
                      <span className="price-current">
                        {item.price_curr} {currentPrice.toFixed(2)}
                      </span>
                      {originalPrice && (
                        <span className="price-original">
                          {item.price_curr} {originalPrice.toFixed(2)}
                        </span>
                      )}
                    </div>
                    
                    {!item.in_stock && (
                      <div className="stock-warning">
                        Currently unavailable
                      </div>
                    )}
                  </div>
                  
                  <div className="wishlist-item-actions">
                    <div className="action-buttons">
                      <button 
                        className="btn-view"
                        onClick={() => viewProduct(item.id)}
                        title="View product"
                      >
                        <RiEyeLine />
                      </button>
                      <button 
                        className="btn-share"
                        onClick={() => shareProduct(item)}
                        title="Share product"
                      >
                        <RiShare2Line />
                      </button>
                      <button 
                        className={`btn-remove ${isRemoving[item.id] ? 'loading' : ''}`}
                        onClick={() => removeFromWishlist(item.id)}
                        title="Remove from wishlist"
                        disabled={isRemoving[item.id]}
                      >
                        {isRemoving[item.id] ? <RiLoader4Line className="spinning" /> : <RiDeleteBin6Line />}
                      </button>
                    </div>
                    
                    <button 
                      className={`btn-add-to-cart ${!item.in_stock ? 'disabled' : ''}`}
                      onClick={() => addToCart(item)}
                      disabled={!item.in_stock}
                    >
                      <RiShoppingCartLine />
                      {item.in_stock ? 'Add to cart' : 'Out of stock'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="wishlist-actions">
            <button 
              className="btn-continue-shopping"
              onClick={() => navigate('/products')}
            >
              Continue shopping
              <RiArrowRightLine />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}